import { useState } from 'react'
import { router } from '@inertiajs/react'
import type { ActionItemChecklistItem } from '../../types'

interface Props {
  actionItemId: number
  items: ActionItemChecklistItem[]
  readOnly?: boolean
}

export default function ActionItemChecklist({ actionItemId, items, readOnly = false }: Props) {
  const [newTitle, setNewTitle] = useState('')

  const sorted = [...items].sort((a, b) => {
    const aDone = a.status === 'complete' ? 1 : 0
    const bDone = b.status === 'complete' ? 1 : 0
    if (aDone !== bDone) return aDone - bDone
    if (a.position !== b.position) return a.position - b.position
    return a.id - b.id
  })

  const completedCount = items.filter(i => i.status === 'complete').length

  const handleAdd = () => {
    if (!newTitle.trim()) return
    router.post(`/action_items/${actionItemId}/checklist_items`, {
      checklist_item: { title: newTitle.trim() },
    }, {
      preserveScroll: true,
      onSuccess: () => setNewTitle(''),
    })
  }

  const handleToggle = (item: ActionItemChecklistItem) => {
    router.post(`/action_items/${actionItemId}/checklist_items/${item.id}/complete`, {}, { preserveScroll: true })
  }

  const handleDelete = (item: ActionItemChecklistItem) => {
    router.delete(`/action_items/${actionItemId}/checklist_items/${item.id}`, { preserveScroll: true })
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-text-primary">
          Checklist
          {items.length > 0 && (
            <span className="font-normal text-text-muted ml-1">
              ({completedCount}/{items.length})
            </span>
          )}
        </h4>
      </div>

      <div className="space-y-0.5">
        {sorted.map(item => {
          const isComplete = item.status === 'complete'
          return (
            <div key={item.id} className="flex items-center gap-3 py-1.5 px-2 rounded-lg hover:bg-[#F5F5F5]/40 group">
              <button
                type="button"
                onClick={() => !readOnly && handleToggle(item)}
                disabled={readOnly}
                className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                  isComplete
                    ? 'bg-[#648E8A] border-[#648E8A] text-white'
                    : 'border-stone/40 hover:border-[#648E8A]'
                } ${readOnly ? 'cursor-default' : ''}`}
              >
                {isComplete && (
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>

              <span className={`flex-1 min-w-0 text-sm ${isComplete ? 'text-text-muted line-through' : 'text-text-primary'}`}>
                {item.title}
              </span>

              {!readOnly && (
                <button
                  type="button"
                  onClick={() => handleDelete(item)}
                  className="opacity-0 group-hover:opacity-100 text-text-muted hover:text-pinnacle-red transition-all flex-shrink-0"
                  aria-label="Remove checklist item"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          )
        })}
      </div>

      {!readOnly && (
        <div className="flex items-center gap-2 mt-2 px-2">
          <input
            type="text"
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
            placeholder="Add a checklist item"
            className="flex-1 px-2 py-1.5 border border-stone/30 rounded text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20"
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); handleAdd() }
            }}
          />
          <button
            type="button"
            onClick={handleAdd}
            disabled={!newTitle.trim()}
            className="text-sm text-pinnacle-red font-medium disabled:opacity-40"
          >
            Add
          </button>
        </div>
      )}
    </div>
  )
}
